import { type FC } from "react";
import { type CassetteProps } from "@userFrontend/features/publication/components/ScoutCassette/type";
import { Salaries } from "@userFrontend/features/publication/components/Salaries";
import { Icon } from "@root/shared/components/Icon";
import { TextButton } from "@root/shared/components/TextButton";
import { Link } from "@root/shared/components/Link";
import { Button } from "@root/shared/components/Button";
import { Label } from "@root/shared/components/Label";
import { ShopWorksModal } from "@userFrontend/features/publication/components/ShopWorksModal";
import { ModalInner } from "@userFrontend/features/publication/components/ScoutCassette/uiParts/ModalInner";
import {
  GA_SCOUT_ASSENT_ACTION,
  GA_SCOUT_EXTEND_ACTION,
} from "@userFrontend/features/publication/components/ScoutCassette/constants";
import * as styles from "./styles.css";
import { PremiumMessage } from "../PremiumMessage";
import { MatchReasons } from "../MatchReasons";

const DEFAULT_SHOW_SALARY_COUNT = 4;

export const CassetteSp: FC<CassetteProps> = ({
  workCode,
  shopId,
  imgSrc,
  labels: { isShowLabel, isDecline, isNewArrivals, isPremiumScout },
  brandName,
  businessType,
  occupation,
  matchReasons,
  message,
  isShopScout,
  shopCount,
  shopName,
  scoutMailUrl,
  title,
  employmentAndSalaryType,
  location,
  entryPath,
  declinePath,
  acceptPath,
  deadline = 0,
  isExtendable,
  extendPath,
  handleOpenModal,
  handleCloseModal,
  handleClickLink,
  handleClickExtendScoutDeadlineButton,
  isOpenModal,
  cassettes,
  isShowIndicator,
  openModalButtonRef,
  indicatorRef,
}) => {
  const scoutMailUrlWithOverrideShopId =
    shopCount > 1 ? `${scoutMailUrl}?override_shop_id=${shopId}` : scoutMailUrl;
  return (
    <div className={styles.cassette}>
      <a href={scoutMailUrlWithOverrideShopId} className={styles.link}>
        {isShowLabel && (isDecline || isNewArrivals || isPremiumScout) && (
          <div className={styles.labelsOuter}>
            {isDecline && <Label theme="gray">辞退</Label>}
            {isNewArrivals && <Label theme="red">新着</Label>}
            {isPremiumScout && (
              <span className={styles.premiumScoutLabel}>
                <Icon name="premium-scout" />
                プレミアム
              </span>
            )}
          </div>
        )}
        <img src={imgSrc} alt={brandName} width={657} height={370} className={styles.image} />
        <div className={styles.container}>
          <p className={styles.brandNameOuter}>
            {brandName}｜{businessType} / {occupation}
          </p>
          {!isPremiumScout && matchReasons && matchReasons.length > 0 && (
            <MatchReasons matchReasons={matchReasons} />
          )}
          {isPremiumScout && message && message?.length > 0 && <PremiumMessage message={message} />}
          <div className={styles.shopNameAndTitleOuter}>
            {isShopScout && shopCount === 1 && (
              <p className={styles.isShopScout}>この店舗からのスカウト</p>
            )}
            <p className={styles.shopName}>
              <span className={styles.shopIcon}>
                <Icon name="location" />
              </span>
              {shopName}
            </p>
            <p className={styles.title}>{title}</p>
          </div>
          <Salaries
            employmentAndSalaryType={employmentAndSalaryType.slice(0, DEFAULT_SHOW_SALARY_COUNT)}
          />
          {employmentAndSalaryType.length > DEFAULT_SHOW_SALARY_COUNT && (
            <p className={styles.otherSalary}>...他</p>
          )}
          <p className={styles.location}>{location}</p>
        </div>
      </a>
      {!isShopScout && shopCount > 1 && (
        <div className={styles.showShopModalOuter}>
          <Button
            type="button"
            theme="tertiary"
            size="small"
            rightIcon={<Icon name="arrow-bold-right" />}
            onClick={handleOpenModal}
            ref={openModalButtonRef}
          >
            <span>
              他 <span className={styles.shopCount}>{shopCount - 1}</span> 店舗の求人を見る
            </span>
          </Button>
        </div>
      )}
      <div className={styles.buttonContainer}>
        {isDecline ? (
          <p className={styles.actionButtonOuter}>
            <Link href={entryPath} theme="primary">
              応募画面に進む
            </Link>
          </p>
        ) : (
          <>
            <p className={styles.actionButtonOuter}>
              <Link
                href={declinePath}
                theme="tertiary"
                className={`${styles.actionButton} js-scout-decline`}
              >
                辞退する
              </Link>
              <Link
                href={acceptPath}
                theme="primary"
                className={`${styles.actionButton} ${styles.acceptButton}`}
                data-ga-action={GA_SCOUT_ASSENT_ACTION}
                data-ga-label={`${workCode}_${shopId}`}
              >
                スカウトを受ける
              </Link>
            </p>
            <p className={styles.extendOuter}>
              有効期限：<span className={styles.extendCount}>あと{deadline}日</span>
              {isExtendable && extendPath && (
                <TextButton
                  type="button"
                  size="large"
                  isBold
                  rightIconName="arrow-bold-right"
                  className={styles.extendLink}
                  onClick={() => handleClickExtendScoutDeadlineButton(extendPath, "", "")}
                  data-ga-action={GA_SCOUT_EXTEND_ACTION}
                  data-ga-label={`${workCode}_${shopId}`}
                >
                  延長する
                </TextButton>
              )}
            </p>
          </>
        )}
      </div>
      {cassettes && cassettes.length > 0 && (
        <ShopWorksModal
          isShow={isOpenModal}
          brandName={brandName}
          businessTypeAndOccupation={`${businessType} / ${occupation}`}
          totalNumber={shopCount}
          handleCloseModal={handleCloseModal}
        >
          <ModalInner
            cassettes={cassettes}
            scoutMailUrl={scoutMailUrl}
            isShowIndicator={isShowIndicator}
            indicatorRef={indicatorRef}
            handleClickLink={handleClickLink}
          />
        </ShopWorksModal>
      )}
      <input type="hidden" name="scout" id="scout" value="true" autoComplete="off" />
    </div>
  );
};
